import { Button, pxToRem, Stack, styled, Text } from '@lessonnine/design-system.lib';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
// External Imports
import { connect } from 'react-redux';

// Internal Imports
import { breakpoints } from '../../config/css/breakpoints.mjs';
import { options } from '../../config/reactRedux.mjs';
import { LANGUAGES } from '../../config/types/prop.mjs';
import { selectEdition } from '../../selectors/selectEdition.mjs';
import { selectPageTrackingData404 } from '../../selectors/selectPageTrackingData.mjs';
import { trackPageEvent } from '../../utils/tracking.mjs';
import { getBabbelHomepageUrl } from '../../utils/url.mjs';
import { I18n } from '../I18n/I18n.jsx';

const FourOhFourNumber = styled.span`
  color: ${({ theme }) => theme.color.surface.foreground.tertiary};
  font-size: ${pxToRem(84)};
  font-weight: 600;

  @media ${breakpoints.desktop.min} {
    font-size: ${pxToRem(120)};
    margin-top: ${pxToRem(16)};
  }
`;

const Divider = styled.div`
  background-color: ${({ theme }) => theme.color.surface.container.separator};
  height: ${pxToRem(1)};
  margin: ${pxToRem(24)} 0;
  width: 80%;

  @media ${breakpoints.desktop.min} {
    width: 70%;
  }
`;

function NotFound({ currentEdition, pageTrackingData404 }) {
  const babbelHomepageUrl = getBabbelHomepageUrl(currentEdition);
  const { data, isLoading } = pageTrackingData404;
  useEffect(() => {
    if (!isLoading) {
      trackPageEvent({ name: 'magazine_page:shown', ...data });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the data object is built in selector getPageTrackingData404(), so instead rely on string page_path which should remain consistent across renders
  }, [data.page_path, isLoading]);

  return (
    <Stack padding={[50, 0, 50, 0]}>
      <Stack align="center" alignItems="center">
        <FourOhFourNumber>404</FourOhFourNumber>
        <Divider />
      </Stack>
      <Stack align="center" alignItems="center" gap={32}>
        <Text fontWeight="700">
          <I18n defaultText="Looks like this page doesn't exist..." field="no-page-found2" />
        </Text>
        <Button as="a" href={babbelHomepageUrl}>
          <I18n defaultText="Go to Babbel Homepage" field="no-page-found3" />
        </Button>
      </Stack>
    </Stack>
  );
}

NotFound.propTypes = {
  currentEdition: LANGUAGES.isRequired,
  pageTrackingData404: PropTypes.shape({
    data: PropTypes.shape({
      page_path: PropTypes.string,
    }),
    isLoading: PropTypes.bool,
  }).isRequired,
  pathMatch: PropTypes.shape({
    query: PropTypes.object, // eslint-disable-line react/forbid-prop-types -- object of key/value pairs
    url: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  currentEdition: selectEdition(state),
  pageTrackingData404: selectPageTrackingData404(state),
});

const withRedux = connect(mapStateToProps, undefined, undefined, options);
const NotFoundHoc = withRedux(NotFound);

// Module Exports
export { NotFoundHoc as NotFound };
